import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { ListingsService } from 'listings/services/listings.service';
import { OutletsService } from 'listings/services/outlets.service';
import { RoomOverviewGeneralService } from 'listings/services/rooms/room-overview-general.service';
import { catchError, combineLatest, of, switchMap } from 'rxjs';
import { ContextAwareSidebarItem } from 'shared/interfaces/context-aware-sidebar.interface';

const LISTING_API = environment.BASE_URL + 'listingApi';

interface BulkEditFeatureGroup {
    id: string;
    name: string;
    fieldName: string;
}

@Injectable({
    providedIn: 'root',
})
export class ContextAwareSidebarService {
    private roomOverviewGeneralService = inject(RoomOverviewGeneralService);

    listingId = signal<string | null>(null);
    listingId$ = toObservable(this.listingId);

    outletId = signal<string | null>(null);
    outletId$ = toObservable(this.outletId);

    bulkOperationId = signal<string | null>(null);
    bulkOperationId$ = toObservable(this.bulkOperationId);

    bulkSupersetListingId = signal<string | null>(null);
    bulkSupersetListingId$ = toObservable(this.bulkSupersetListingId);

    bulkListingCount = signal<number | null>(null);

    private selectedRoomId$ = this.roomOverviewGeneralService.selectedRoomId$;
    selectedRoomId = this.roomOverviewGeneralService.selectedRoomId;

    sidebarData = toSignal(
        combineLatest([this.listingId$, this.outletId$, this.selectedRoomId$, this.bulkOperationId$, this.bulkSupersetListingId$]).pipe(
            switchMap(([listingId, outletId, roomId, bulkOperationId, bulkSupersetListingId]) => {
                if (bulkOperationId && bulkSupersetListingId) {
                    return this.loadBulkNavigationItems(bulkOperationId, bulkSupersetListingId);
                }

                if (!listingId) {
                    return of<ContextAwareSidebarItem[]>([]);
                }

                if (roomId) {
                    const roomPath = new URL(`${LISTING_API}/settings/room-navigation-items`);
                    roomPath.searchParams.append('listingId', listingId);
                    roomPath.searchParams.append('roomId', roomId);
                    return this.http.get<ContextAwareSidebarItem[]>(roomPath.toString()).pipe(
                        catchError((error) => {
                            console.error('Failed to load room sidebar navigation items', error);
                            return of<ContextAwareSidebarItem[]>([]);
                        }),
                    );
                }

                const path = new URL(`${LISTING_API}/settings/navigation-items`);
                path.searchParams.append('listingId', listingId);
                if (outletId) {
                    path.searchParams.append('outletId', outletId);
                }
                return this.http.get<ContextAwareSidebarItem[]>(path.toString()).pipe(
                    catchError((error) => {
                        console.error('Failed to load sidebar navigation items', error);
                        return of<ContextAwareSidebarItem[]>([]);
                    }),
                );
            }),
        ),
        {
            initialValue: [] as ContextAwareSidebarItem[],
        },
    );

    listing = toSignal(
        this.listingId$.pipe(
            switchMap((listingId) => {
                if (!listingId) {
                    return of(null);
                }
                return this.listingsService.getListingById(listingId).pipe(
                    catchError((error) => {
                        console.error('Failed to load listing for sidebar', error);
                        return of(null);
                    }),
                );
            }),
        ),
    );

    outlet = toSignal(
        combineLatest([this.listingId$, this.outletId$]).pipe(
            switchMap(([listingId, outletId]) => {
                if (!listingId || !outletId) {
                    return of(null);
                }
                return this.outletService.getOutlet(listingId, outletId).pipe(
                    catchError((error) => {
                        console.error('Failed to load outlet for sidebar', error);
                        return of(null);
                    }),
                );
            }),
        ),
    );

    roomName = this.roomOverviewGeneralService.atAGlanceData;

    constructor(
        private http: HttpClient,
        private listingsService: ListingsService,
        private outletService: OutletsService,
    ) {}

    private loadBulkNavigationItems(bulkOperationId: string, supersetListingId: string) {
        const navigationItemsPath = new URL(`${LISTING_API}/settings/navigation-items`);
        navigationItemsPath.searchParams.append('listingId', supersetListingId);

        const bulkFeatureGroupsPath = new URL(`${LISTING_API}/settings/bulk-get-edit-feature-groups`);
        bulkFeatureGroupsPath.searchParams.append('bulkOperationId', bulkOperationId);

        return combineLatest([
            this.http.get<ContextAwareSidebarItem[]>(navigationItemsPath.toString()).pipe(
                catchError((error) => {
                    console.error('Failed to load bulk sidebar navigation items', error);
                    return of<ContextAwareSidebarItem[]>([]);
                }),
            ),
            this.http.get<BulkEditFeatureGroup[]>(bulkFeatureGroupsPath.toString()).pipe(
                catchError((error) => {
                    console.error('Failed to load bulk edit feature groups', error);
                    return of<BulkEditFeatureGroup[]>([]);
                }),
            ),
        ]).pipe(
            switchMap(([navigationItems, bulkFeatureGroups]) => {
                const allowedFieldNames = new Set(bulkFeatureGroups.map((featureGroup) => featureGroup.fieldName));
                return of(this.filterNavigationItemsByFieldNames(navigationItems, allowedFieldNames));
            }),
        );
    }

    private filterNavigationItemsByFieldNames(
        navigationItems: ContextAwareSidebarItem[],
        allowedFieldNames: Set<string>,
    ): ContextAwareSidebarItem[] {
        return navigationItems.reduce<ContextAwareSidebarItem[]>((accumulatedItems, item) => {
            const originalChildren = item.children ?? [];
            const filteredChildren = originalChildren.filter((child) => child.fieldName !== null && allowedFieldNames.has(child.fieldName));

            const parentIsAllowed = item.fieldName !== null && allowedFieldNames.has(item.fieldName);

            if (!parentIsAllowed && filteredChildren.length === 0) {
                return accumulatedItems;
            }

            const removedChildrenCount = originalChildren.length - filteredChildren.length;
            const adjustedTotalChildrenCount =
                item.totalChildrenCount != null ? item.totalChildrenCount - removedChildrenCount : item.totalChildrenCount;

            accumulatedItems.push({
                ...item,
                children: filteredChildren,
                totalChildrenCount: adjustedTotalChildrenCount,
            });

            return accumulatedItems;
        }, []);
    }
}
